import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const NetworkContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: 0,
  opacity: theme.palette.mode === 'dark' ? 0.4 : 0.2,
  pointerEvents: 'none'
}));

const NetworkCanvas = styled('canvas')({
  width: '100%',
  height: '100%',
  display: 'block'
});

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  connections: number[];
  pulse: number;
  pulseDirection: number;
}

const BlockchainNetwork: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const nodes: Node[] = [];
    const nodeCount = 15;
    const maxDistance = 150;

    // Initialize nodes function
    const initNodes = () => {
      nodes.length = 0;
      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          radius: 3 + Math.random() * 4,
          connections: [],
          pulse: Math.random() * Math.PI * 2,
          pulseDirection: 1
        });
      }

      // Create connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const distance = Math.sqrt(
            Math.pow(nodes[i].x - nodes[j].x, 2) + 
            Math.pow(nodes[i].y - nodes[j].y, 2)
          );
          if (distance < maxDistance && Math.random() < 0.3) {
            nodes[i].connections.push(j);
          }
        }
      }
    };

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      initNodes();
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Mouse tracking
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };
    canvas.addEventListener('mousemove', handleMouseMove);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw nodes
      nodes.forEach((node, index) => {
        // Update position
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off edges
        if (node.x <= 0 || node.x >= canvas.width) node.vx *= -1;
        if (node.y <= 0 || node.y >= canvas.height) node.vy *= -1;

        // Keep nodes in bounds
        node.x = Math.max(0, Math.min(canvas.width, node.x));
        node.y = Math.max(0, Math.min(canvas.height, node.y));

        // Update pulse
        node.pulse += 0.05 * node.pulseDirection;
        if (node.pulse > Math.PI * 2) {
          node.pulse = 0;
        }

        // Mouse interaction
        const mouseDistance = Math.sqrt(
          Math.pow(node.x - mousePos.x, 2) + 
          Math.pow(node.y - mousePos.y, 2)
        );
        const isNearMouse = mouseDistance < 100;

        // Draw connections
        node.connections.forEach(connectionIndex => {
          const connectedNode = nodes[connectionIndex];
          if (connectedNode) {
            const distance = Math.sqrt(
              Math.pow(node.x - connectedNode.x, 2) + 
              Math.pow(node.y - connectedNode.y, 2)
            );
            
            if (distance < maxDistance) {
              const opacity = 1 - (distance / maxDistance);
              const lineWidth = isNearMouse ? 2 : 1;
              
              ctx.beginPath();
              ctx.moveTo(node.x, node.y);
              ctx.lineTo(connectedNode.x, connectedNode.y);
              ctx.strokeStyle = isNearMouse 
                ? `rgba(0, 212, 170, ${opacity * 0.8})` 
                : `rgba(0, 102, 204, ${opacity * 0.4})`;
              ctx.lineWidth = lineWidth;
              ctx.stroke();

              // Data packet animation
              if (Math.random() < 0.01) {
                const packetX = node.x + (connectedNode.x - node.x) * Math.random();
                const packetY = node.y + (connectedNode.y - node.y) * Math.random();
                
                ctx.beginPath();
                ctx.arc(packetX, packetY, 2, 0, Math.PI * 2);
                ctx.fillStyle = '#00ff41';
                ctx.fill();
              }
            }
          }
        });

        // Draw node
        const pulseSize = 1 + Math.sin(node.pulse) * 0.3;
        const nodeRadius = node.radius * (isNearMouse ? 1.5 : 1) * pulseSize;
        
        // Node glow
        const gradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, nodeRadius * 2
        );
        gradient.addColorStop(0, isNearMouse ? 'rgba(0, 212, 170, 0.8)' : 'rgba(0, 102, 204, 0.6)');
        gradient.addColorStop(1, 'rgba(0, 102, 204, 0)');
        
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeRadius * 2, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Node core
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeRadius, 0, Math.PI * 2);
        ctx.fillStyle = isNearMouse ? '#00d4aa' : '#0066cc';
        ctx.fill();

        // Node border
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeRadius, 0, Math.PI * 2);
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [mousePos.x, mousePos.y]);

  return (
    <NetworkContainer>
      <NetworkCanvas ref={canvasRef} />
    </NetworkContainer>
  );
};

export default BlockchainNetwork;